export const BUILD_AUTHORITY_PROTOCOL = 'testcs.build.v1'
export const BUILD_AUTHORITY_ENTITY_ID = 'testcs-build-authority'

const EMPTY_SNAPSHOT = Object.freeze({ revision: 0, placements: [], plotId: null, plotVersion: null, capabilities: [], isOwner: false, authorityOnline: false })

const clone = value => JSON.parse(JSON.stringify(value))
const makeId = prefix => `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`

function requireRevision(baseRevision) {
  if (!Number.isInteger(baseRevision) || baseRevision < 0) throw new TypeError('baseRevision must be a non-negative integer')
}

function requirePlotRef(plotId, plotVersion) {
  if (typeof plotId !== 'string' || !plotId || !Number.isInteger(plotVersion) || plotVersion < 1) throw new TypeError('plotId and plotVersion are required')
}

export function createPlacementCommand({ baseRevision, plotId, plotVersion, catalogId, item, transform, commandId = makeId('place'), issuedAt = new Date().toISOString() }) {
  requireRevision(baseRevision)
  requirePlotRef(plotId, plotVersion)
  if (!item?.id || !catalogId) throw new TypeError('catalogId and item are required')
  return {
    protocol: BUILD_AUTHORITY_PROTOCOL,
    type: item.kind === 'blueprint' ? 'placement.create-blueprint' : 'placement.create-module',
    commandId,
    baseRevision,
    plotId,
    plotVersion,
    issuedAt,
    payload: {
      catalogId,
      moduleId: item.kind === 'blueprint' ? null : item.id,
      blueprintId: item.kind === 'blueprint' ? item.blueprintId : null,
      transform: clone(transform),
      preview: {
        id: item.id,
        displayName: item.displayName,
        dimensionsM: clone(item.dimensionsM),
        boundsOffsetM: clone(item.boundsOffsetM || null),
        collider: clone(item.collider || null),
        snapPoints: clone(item.snapPoints || []),
      },
    },
  }
}

export function createUndoCommand({ baseRevision, plotId, plotVersion, targetCommandId, objectVersion, commandId = makeId('undo'), issuedAt = new Date().toISOString() }) {
  requireRevision(baseRevision)
  requirePlotRef(plotId, plotVersion)
  if (!targetCommandId || !Number.isInteger(objectVersion) || objectVersion < 1) throw new TypeError('targetCommandId and objectVersion are required')
  return { protocol: BUILD_AUTHORITY_PROTOCOL, type: 'placement.undo', commandId, baseRevision, plotId, plotVersion, issuedAt, payload: { targetCommandId, objectVersion } }
}

export function createPlacementPreviewCommand({ baseRevision, plotId, plotVersion, catalogId, item = null, target = null, transform, commandId = makeId('preview'), issuedAt = new Date().toISOString() }) {
  requireRevision(baseRevision)
  requirePlotRef(plotId, plotVersion)
  if (!catalogId || (!item?.id && !target?.id)) throw new TypeError('catalogId and item or target are required')
  return {
    protocol: BUILD_AUTHORITY_PROTOCOL,
    type: 'placement.preview',
    commandId,
    baseRevision,
    plotId,
    plotVersion,
    issuedAt,
    payload: {
      mode: target ? 'edit' : 'create',
      catalogId,
      targetPlacementId: target?.id || null,
      objectVersion: target?.version || null,
      moduleId: target ? null : item.kind === 'blueprint' ? null : item.id,
      blueprintId: target ? null : item.kind === 'blueprint' ? item.blueprintId : null,
      transform: clone(transform),
    },
  }
}

export function createPlacementEditCommand({ baseRevision, plotId, plotVersion, targetPlacementId, objectVersion, transform, commandId = makeId('edit'), issuedAt = new Date().toISOString() }) {
  requireRevision(baseRevision)
  requirePlotRef(plotId, plotVersion)
  if (!targetPlacementId || !Number.isInteger(objectVersion) || objectVersion < 1) throw new TypeError('targetPlacementId and objectVersion are required')
  return { protocol: BUILD_AUTHORITY_PROTOCOL, type: 'placement.update', commandId, baseRevision, plotId, plotVersion, issuedAt, payload: { targetPlacementId, objectVersion, transform: clone(transform) } }
}

export function createPlacementDeleteCommand({ baseRevision, plotId, plotVersion, targetPlacementId, objectVersion, commandId = makeId('delete'), issuedAt = new Date().toISOString() }) {
  requireRevision(baseRevision)
  requirePlotRef(plotId, plotVersion)
  if (!targetPlacementId || !Number.isInteger(objectVersion) || objectVersion < 1) throw new TypeError('targetPlacementId and objectVersion are required')
  return { protocol: BUILD_AUTHORITY_PROTOCOL, type: 'placement.delete', commandId, baseRevision, plotId, plotVersion, issuedAt, payload: { targetPlacementId, objectVersion } }
}

export function assertBuildAdapter(adapter) {
  for (const method of ['getSnapshot', 'subscribe', 'submit']) {
    if (typeof adapter?.[method] !== 'function') throw new TypeError(`Build adapter must implement ${method}()`)
  }
  return adapter
}

export function createBuildAuthorityAdapter(adapter) {
  return assertBuildAdapter({ mode: 'server', persistent: true, ...adapter })
}

/**
 * Bridges the React Build Mode to the hidden, server-executed authority app.
 * The authority app owns validation and persistence; the browser only keeps a
 * mirrored snapshot and never writes arbitrary world entities.
 */
export function createWorldBuildAdapter({
  world,
  entityId = BUILD_AUTHORITY_ENTITY_ID,
  timeoutMs = 6000,
  retryMs = 250,
  healthMs = 5000,
} = {}) {
  if (!world?.entities?.get) throw new TypeError('world.entities.get is required')

  let snapshot = clone(EMPTY_SNAPSHOT)
  let authority = null
  let authorityVersion = null
  let retryTimer = null
  let probePending = null
  let nextProbeAt = 0
  let online = false
  const listeners = new Set()
  const pending = new Map()

  const publish = next => {
    if (!next || !Number.isInteger(next.revision) || !Array.isArray(next.placements)) return
    snapshot = { ...clone(next), authorityOnline: next.authorityOnline ?? online }
    listeners.forEach(listener => listener(clone(snapshot)))
  }

  const setOnline = value => {
    if (online === value && snapshot.authorityOnline === value) return
    online = value
    publish({ ...snapshot, authorityOnline: value })
  }

  const onSnapshot = message => {
    if (message?.protocol !== BUILD_AUTHORITY_PROTOCOL) return
    probePending = null
    nextProbeAt = Date.now() + healthMs
    online = true
    publish({ ...message.snapshot, authorityOnline: true })
  }

  const onResult = result => {
    if (result?.protocol !== BUILD_AUTHORITY_PROTOCOL || !result.commandId) return
    probePending = null
    nextProbeAt = Date.now() + healthMs
    online = true
    if (result.snapshot) publish(result.snapshot)
    const request = pending.get(result.commandId)
    if (!request) return
    pending.delete(result.commandId)
    clearTimeout(request.timer)
    request.resolve(clone(result))
  }

  const unbind = () => {
    if (!authority) return
    authority.off?.('build:snapshot', onSnapshot)
    authority.off?.('build:result', onResult)
    authority = null
    authorityVersion = null
  }

  const bind = () => {
    const next = world.entities.get(entityId)
    if (!next?.getAppProxy || !next?.on || next.building || !next.blueprint || next.blueprint.disabled || next.scriptError) {
      unbind()
      return false
    }
    // App rebuilds clear entity listeners in-place. Reattach on every probe,
    // even when the entity object and blueprint version did not change.
    unbind()
    authority = next
    authorityVersion = next.blueprint.version
    authority.on('build:snapshot', onSnapshot)
    authority.on('build:result', onResult)
    return true
  }

  const send = (name, data) => {
    if (!bind()) return false
    try {
      const proxy = authority.getAppProxy()
      if (typeof proxy?.send !== 'function') return false
      proxy.send(name, data)
      return true
    } catch {
      unbind()
      return false
    }
  }

  const probe = (force = false) => {
    if (!listeners.size) return false
    const now = Date.now()
    const current = world.entities.get(entityId)
    if (
      authority &&
      (current !== authority || current?.building || !current?.blueprint || current.blueprint.version !== authorityVersion || current.scriptError)
    ) {
      unbind()
      probePending = null
      nextProbeAt = now
      setOnline(false)
    }
    if (probePending && now - probePending.startedAt >= timeoutMs) {
      probePending = null
      nextProbeAt = now + retryMs
      setOnline(false)
    }
    if (probePending || (!force && now < nextProbeAt)) return false
    const requestId = makeId('snapshot')
    probePending = { requestId, startedAt: now }
    if (!send('build:snapshot:request', { protocol: BUILD_AUTHORITY_PROTOCOL, requestId })) {
      probePending = null
      nextProbeAt = now + retryMs
      setOnline(false)
      return false
    }
    return true
  }

  const ensureProbeLoop = () => {
    if (retryTimer) return
    retryTimer = setInterval(() => probe(), retryMs)
  }

  return createBuildAuthorityAdapter({
    getSnapshot: () => clone(snapshot),
    subscribe(listener) {
      listeners.add(listener)
      listener(clone(snapshot))
      ensureProbeLoop()
      probe(true)
      return () => {
        listeners.delete(listener)
        if (listeners.size) return
        if (retryTimer) clearInterval(retryTimer)
        retryTimer = null
        probePending = null
        unbind()
      }
    },
    submit(command) {
      if (!command?.commandId) return Promise.resolve({ status: 'rejected', code: 'INVALID_COMMAND', snapshot: clone(snapshot) })
      return new Promise(resolve => {
        const timer = setTimeout(() => {
          pending.delete(command.commandId)
          setOnline(false)
          nextProbeAt = Date.now()
          resolve({
            protocol: BUILD_AUTHORITY_PROTOCOL,
            status: 'rejected',
            commandId: command.commandId,
            code: 'AUTHORITY_TIMEOUT',
            snapshot: clone(snapshot),
          })
        }, timeoutMs)
        pending.set(command.commandId, { resolve, timer })
        if (send('build:command', command)) return
        clearTimeout(timer)
        pending.delete(command.commandId)
        resolve({
          protocol: BUILD_AUTHORITY_PROTOCOL,
          status: 'rejected',
          commandId: command.commandId,
          code: 'AUTHORITY_UNAVAILABLE',
          snapshot: clone(snapshot),
        })
      })
    },
  })
}

export function createLocalBuildAdapter({ latencyMs = 80, initialPlacements = [] } = {}) {
  let snapshot = { revision: 0, placements: clone(initialPlacements).map(item => ({ version: 1, ...item })), plotId: 'local', plotVersion: 1, capabilities: ['build.place', 'build.edit', 'build.delete'], isOwner: true }
  const listeners = new Set()
  const publish = () => listeners.forEach(listener => listener(clone(snapshot)))
  const reject = (command, code) => ({ status: 'rejected', commandId: command.commandId, code, snapshot: clone(snapshot) })
  return assertBuildAdapter({
    mode: 'local-dev',
    persistent: false,
    getSnapshot: () => clone(snapshot),
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    async submit(command) {
      if (latencyMs) await new Promise(resolve => setTimeout(resolve, latencyMs))
      if (command.protocol !== BUILD_AUTHORITY_PROTOCOL) return reject(command, 'UNSUPPORTED_PROTOCOL')
      if (command.baseRevision !== snapshot.revision) return reject(command, 'REVISION_CONFLICT')
      if (command.type === 'placement.preview') {
        return { status: 'preview', commandId: command.commandId, preview: { valid: true, transform: clone(command.payload.transform) }, snapshot: clone(snapshot) }
      }
      if (command.type === 'placement.undo') {
        const index = snapshot.placements.findIndex(item => item.sourceCommandId === command.payload.targetCommandId)
        if (index === -1) return reject(command, 'UNDO_TARGET_NOT_FOUND')
        const placements = snapshot.placements.slice()
        placements.splice(index, 1)
        snapshot = { ...snapshot, revision: snapshot.revision + 1, placements }
      } else if (command.type === 'placement.delete') {
        const index = snapshot.placements.findIndex(item => item.id === command.payload.targetPlacementId)
        if (index === -1) return reject(command, 'OBJECT_NOT_FOUND')
        if (snapshot.placements[index].version !== command.payload.objectVersion) return reject(command, 'OBJECT_VERSION_CONFLICT')
        snapshot = { ...snapshot, revision: snapshot.revision + 1, placements: snapshot.placements.filter((_, itemIndex) => itemIndex !== index) }
      } else if (command.type === 'placement.update') {
        const index = snapshot.placements.findIndex(item => item.id === command.payload.targetPlacementId)
        if (index === -1) return reject(command, 'OBJECT_NOT_FOUND')
        if (snapshot.placements[index].version !== command.payload.objectVersion) return reject(command, 'OBJECT_VERSION_CONFLICT')
        const placements = snapshot.placements.slice()
        placements[index] = { ...placements[index], transform: clone(command.payload.transform), version: placements[index].version + 1 }
        snapshot = { ...snapshot, revision: snapshot.revision + 1, placements }
      } else if (command.type === 'placement.create-module' || command.type === 'placement.create-blueprint') {
        const placement = {
          id: makeId('local-placement'),
          sourceCommandId: command.commandId,
          kind: command.type === 'placement.create-blueprint' ? 'blueprint' : 'module',
          moduleId: command.payload.moduleId,
          blueprintId: command.payload.blueprintId,
          transform: clone(command.payload.transform),
          preview: clone(command.payload.preview),
          version: 1,
          provenance: { source: 'catalog', catalogId: command.payload.catalogId },
        }
        snapshot = { ...snapshot, revision: snapshot.revision + 1, placements: [...snapshot.placements, placement] }
      } else return reject(command, 'UNSUPPORTED_COMMAND')
      publish()
      return { status: 'accepted', commandId: command.commandId, revision: snapshot.revision, snapshot: clone(snapshot) }
    },
  })
}
